import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from "react-router";
import orderService from "../services/orderService";
import { format, parseISO } from "date-fns";
import PageFooter from '../components/ui/PageFooter/PageFooter';
import storeService from '../services/storeService';
import { Range, getTrackBackground } from "react-range";
import { useLocationContext } from '../context/LocationContext';
import storeDetailsService from '../services/storeDetailsService';
import GoogleMapComponent from '../components/ui/GoogleMap/GoogleMap';
import { ArrowUpRightFromSquare, Contact, Link, MapPin, Phone, Clock, ArrowRight } from 'lucide-react';
import StatusBadge from '../components/ui/order/StatusBadge';
import HubspotForm from '../components/ui/hubspot/form';


const nzCurrency = (v) =>
  new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD" }).format(
    typeof v === "string" ? parseFloat(v) : v
  );


const Order = () => {
  const params = useParams();
  const location = useLocation();
  const { coords } = useLocationContext();
  // GOD I LOVE NO TYPES.
  const [order, setOrder] = React.useState({});
  const [store, setStore] = React.useState({});
  const [values, setValues] = React.useState([0]);
  const [orderSliderComplete, setOrderSliderComplete] = useState(false);
  const STEP = 1;
  const MIN = 0;
  const MAX = 100;

  const [showSupportForm, setShowSupportForm] = useState(false);

  // Helper functions for pickup times
  const formatTime = (time) => {
    if (!time) return 'Available';
    const [hours, minutes] = time.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHour = hours % 12 || 12;
    return `${displayHour}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Today';
    const date = new Date(dateString);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return 'Tomorrow';
    } else {
      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric'
      });
    }
  };

  // Helper function to get unique pickup time slots from order items
  const getPickupTimeSlots = () => {
    const timeSlots = [];

    if (!order?.items) return timeSlots;

    order.items.forEach(item => {
      // Check if the item has collection_window data
      const collectionWindow = item.product?.collection_window;
      if (collectionWindow) {
        const startTime = collectionWindow.start_time || collectionWindow.start;
        const endTime = collectionWindow.end_time || collectionWindow.end;
        const date = collectionWindow.date;

        if (startTime && endTime) {
          const timeSlot = {
            start: startTime,
            end: endTime,
            date: date,
            items: [item.product?.title]
          };

          // Check if this time slot already exists
          const existingSlot = timeSlots.find(slot =>
            slot.start === startTime &&
            slot.end === endTime &&
            slot.date === date
          );

          if (existingSlot) {
            existingSlot.items.push(item.product?.title);
          } else {
            timeSlots.push(timeSlot);
          }
        }
      }
    });

    return timeSlots;
  };

  useEffect(() => {
    const get = async () => {
      try {
        const { publicOrderId } = params
        const specificOrder = await orderService.Get(publicOrderId);
        console.log('Order:', specificOrder);

        setOrder(specificOrder.data);

        if (specificOrder.data.store && specificOrder.data.store.public_id) {
          const storedata = await storeDetailsService.getStoreDetails(
            specificOrder.data.store.public_id,
            coords?.latitude,
            coords?.longitude,
          );
          setStore(storedata);
        }
      } catch (error) {
        console.error('Error fetching order or store:', error);
      }
    };
    get();
  }, []);

  const created = order?.created_at
    ? format(parseISO(order.created_at), "EEE d MMM, h:mm a")
    : "";

  const redeemed =
    order?.redeemed_at ? format(parseISO(order.redeemed_at), "EEE d MMM, h:mm a") : null;

  const pickupTimeSlots = getPickupTimeSlots();

  useEffect(() => {
    if (orderSliderComplete) {
      redeemOrder();
    }
  }, [orderSliderComplete])

  const redeemOrder = async () => {
    try {
      const { publicOrderId } = params
      const redeemOrder = await orderService.Redeem(publicOrderId);

      setOrder(redeemOrder.data);
    } catch (error) {
      console.error('Error fetching order or store:', error);
    }
  };

  const handleRangeChange = (values) => {
    setValues(values)

    if (values[0] === 100) {
      setOrderSliderComplete(true);
    }
  }

  if (!order || !order.items) {
    return (
      <div className='min-h-screen bg-gg-cream flex justify-center items-center'>
        <p className="text-gg-dark">Loading order...</p>
      </div>
    )
  }
  return (
    <div className='min-h-screen bg-gg-cream flex justify-center'>
      <section className="w-full max-w-screen-sm mx-auto p-4 text-gg-dark mt-12 mb-20">
        {/* Header */}
        <header className="mb-2">
          <h1 className="font-gg-heading text-2xl uppercase text-gg-green mb-4">{order.store?.name}</h1>
          <div className="mt-1 flex items-center gap-2 mb-2">
            <StatusBadge status={order.status} />
            <span className="text-xs bg-white px-2 py-1 rounded uppercase">
              #{String(order.public_id || "").slice(0, 6)}
            </span>
          </div>
          {created && <p className="mt-1 text-sm text-gg-dark/70">Created {created}</p>}
          {redeemed && <p className="text-sm text-gg-dark/70">Redeemed {redeemed}</p>}
        </header>

        {/* Redeem code */}
        {order.redeem_code && order.status === "REDEEMED" && (
          <div className="my-10 flex justify-center flex-col items-center w-full">
            <div className="font-gg-medium text-lg mb-1">Redemption code</div>

            <code className="text-5xl tracking-wider bg-white px-6 py-3 rounded-md font-mono">
              {order.redeem_code}
            </code>
            <p className='text-gg-dark text-sm mt-2'>Show this code to the store when collecting your order.</p>

          </div>
        )}

        {/* TODO: make this only show up when we are in the collection window. */}
        {/* Range slider for pickup */}
        {order.status === "PAID"
          ?
          <>
            <div className="pt-5 px-3 flex flex-col gap-2">
              <p>Look at you go! You’ve just scored yourself an awesome deal in the name of reducing food waste. See, we told you - binning food is boring.
              </p>
              <p>
                All you need to do now is head to {order.store && order.store.name ? order.store.name : "the store"} at the set collection window and swipe to redeem when you’re there. Once you swipe, we reveal your unique redemption code - show this to the store and you’re away laughing.
              </p>
              <p>
                If you have any issues, well not any issues, just issues that we can help you with please contact us <a href="#" onClick={(e) => {
                  e.preventDefault();
                  setShowSupportForm(true);
                }} className="text-gg-green underline">
                  here
                </a> or at <a className="text-gg-green underline" href="mailto:hello@gonegood.co.nz">hello@gonegood.co.nz</a>
              </p>
            </div>

            <div className="my-10 mx-10">
              <Range
                step={STEP}
                min={MIN}
                max={100}
                disabled={values[0] === 100 ? true : false}
                values={values}
                onChange={(values) => handleRangeChange(values)}
                renderTrack={({ props, children }) => {
                  return (
                    <div
                      {...props}
                      style={{
                        ...props.style,
                        background: getTrackBackground({
                          values,
                          colors: ['#00cb77', '#fff'],
                          min: MIN,
                          max: MAX,
                        }),
                        position: 'relative',
                      }}
                      className='rounded-md h-[44px] border-gg-dark'
                    >
                      {children}
                      <div
                        style={{
                          color: "white",
                          fontWeight: "bold",
                          pointerEvents: "none",
                        }}
                        className='flex justify-center items-center absolute w-full h-full z-10 text-gg-dark'
                      >
                        Swipe to redeem
                      </div>
                    </div>
                  );
                }}
                renderThumb={({ props }) => (
                  <div
                    {...props}
                    key={props.key}
                    style={{
                      ...props.style,
                    }}
                    className='flex cursor-pointer justify-center items-center bg-gg-dark rounded-full shadow-lg z-20 h-[44px] w-[44px]'
                  >
                    <ArrowRight size={24} className="text-white mx-auto my-auto" />

                  </div>
                )}
              />
            </div>
          </>
          : <></>}

        {/* Pickup Information Section - NEW */}
        {(order.status === "PAID" || order.status === "REDEEMED") && pickupTimeSlots.length > 0 && (
          <div className="mb-3">
            <div className="font-gg-medium text-sm mb-1 flex items-center gap-2">
              <Clock size={16} className="text-gg-green" />
              Pickup Information
            </div>
            <div className="bg-green-50 rounded p-3 border border-green-200">
              <div className="space-y-2">
                {pickupTimeSlots.map((slot, index) => (
                  <div key={index}>
                    <div className="text-gg-green font-gg-medium text-sm">
                      {formatDate(slot.date)} • {formatTime(slot.start)} - {formatTime(slot.end)}
                    </div>
                    <div className="text-xs text-gg-dark/70 mt-1">
                      Items: {slot.items.join(', ')}
                    </div>
                  </div>
                ))}
              </div>

              {/* Store Address in Pickup Section */}
              {order.store?.address && (
                <div className="mt-3 pt-2 border-t border-green-200">
                  <div className="flex items-start gap-2">
                    <MapPin size={14} className="text-gg-green mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="text-gg-dark font-gg-medium text-sm">{order.store.name}</div>
                      <a
                        href={"https://www.google.com/maps/dir/?api=1&destination=" + encodeURIComponent(order.store.address ? order.store.address : "")}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gg-dark/70 text-xs underline"
                      >
                        {order.store.address}
                      </a>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {
          store && order.status === "PAID" ? <StoreDetailsSection storeData={store} lat={coords?.latitude} lng={coords?.longitude} /> : <></>
        }

        {/* Items */}
        <div className="mb-3">
          <div className="font-gg-medium text-sm mb-1">Items</div>
          <ul className="divide-y divide-gg-dark/10 rounded-md overflow-hidden bg-white">
            {(order.items || []).map((it) => {
              const unit = it?.unit_price ?? it?.product?.price ?? 0;
              const rrp = it?.product?.rrp;
              const hasRrp =
                rrp != null &&
                parseFloat(String(rrp)) > parseFloat(String(unit));

              // Get collection window for individual item
              const itemCollectionWindow = it.product?.collection_window;

              return (
                <li key={it.public_id} className="p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="font-gg-medium text-base leading-5 truncate">
                        {it.product?.title}
                      </div>
                      <div className="text-sm text-gg-dark/70 mt-0.5">
                        Qty {it.quantity}
                        <span className="mx-1">·</span>
                        Unit {nzCurrency(unit)}
                        {hasRrp && (
                          <span className="ml-2 line-through text-gg-dark/50">
                            {nzCurrency(rrp)}
                          </span>
                        )}
                      </div>

                      {/* Individual Item Pickup Time - NEW */}
                      {itemCollectionWindow && (
                        <div className="flex items-center gap-1 mt-1">
                          <Clock size={12} className="text-gg-green" />
                          <span className="text-gg-green text-xs">
                            Pick up: {formatTime(itemCollectionWindow.start_time || itemCollectionWindow.start)} - {formatTime(itemCollectionWindow.end_time || itemCollectionWindow.end)}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="text-sm shrink-0">{nzCurrency(it.subtotal || 0)}</div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Totals */}
        <div className="mb-3">
          <div className="font-gg-medium text-sm mb-1">Summary</div>
          <div className="bg-white rounded-md p-3">
            <div className="flex justify-between text-sm">
              <span>Subtotal</span>
              <span>{nzCurrency(order.subtotal || 0)}</span>
            </div>

            <DiscountHack order={order} />
            <div className="flex justify-between text-base font-gg-medium mt-1">
              <span>Total</span>
              <span>{nzCurrency(order.total_amount || 0)}</span>
            </div>
          </div>
        </div>

        {/* Cancellation note */}
        {order.status === "CANCELLED" && order.cancel_reason && (
          <p className="mt-2 text-xs text-gg-dark/70">Cancelled — {order.cancel_reason}</p>
        )}
      </section>
      <PageFooter />
      <HubspotForm showForm={showSupportForm} setShowForm={setShowSupportForm} formKey="customer-support" />
    </div>
  );
}
const DiscountHack = ({ order }) => {
  const parseAmount = (value) => {
    if (typeof value === "number") return value;
    if (typeof value === "string") return parseFloat(value) || 0;
    return 0;
  };

  const total = parseAmount(order.total_amount);
  const subtotal = parseAmount(order.subtotal);

  const hasDiscount = Math.abs(total - subtotal) > 0.009;
  if (!hasDiscount) return null;

  const discount = total - subtotal;

  try {
    return (
      <div className="flex justify-between text-sm">
        <span>Promotion</span>
        <span>{nzCurrency(discount)}</span>
      </div>
    );
  } catch (err) {
    console.error("DiscountHack render error:", err);
    return null;
  }
};
const StoreDetailsSection = ({ storeData, lat, lng }) => {
  return (
    <>
      <h2 className="font-gg-medium text-sm mb-1">Store Details</h2>
      <div className="flex flex-col gap-1 bg-white rounded mb-3">
        <GoogleMapComponent
          mapContainerStyle={{ width: "100%", height: "10em", borderRadius: "0.25em 0.25em 0 0" }}
          center={{ lat, lng }}
          locationsArray={[
            {
              lat: parseFloat(storeData.latitude),
              lng: parseFloat(storeData.longitude),
            },
          ]}
        />
        <div className="px-3 pb-2 flex flex-col">
          <p className="font-gg-medium text-gg-dark">Location</p>
          <a href={"https://www.google.com/maps/dir/?api=1&destination=" + encodeURIComponent(storeData.address ? storeData.address : "")} target="_blank" rel="noopener noreferrer" className="flex gap-2 flex-wrap items-center">
            <p className="text-black text-base">{storeData.address}</p>
            <ArrowUpRightFromSquare size="16" className="stroke-gg-green" />
          </a>
          {
            storeData.more_info && storeData.more_info.phone_number ? (
              <div className='flex flex-col mt-2'>
                <p className="font-gg-medium text-gg-dark">Phone</p>
                <a
                  href={`tel:${storeData.more_info.phone_number}`}
                  className="text-black text-base"
                >
                  {storeData.more_info.phone_number}
                </a>
              </div>
            ) : <></>
          }
        </div>
      </div>
    </>
  )
}

export default Order;